import axios from 'axios';
import { getStatusForValue, getRangeStringForBiomarker, fatLossBiomarkers } from '../config/fatLossConfig';

const API_BASE_URL = 'https://vitaldev.vitalinsights.in/admin/fatloss-reports';
const DEXA_API_URL = 'https://vitaldev.vitalinsights.in/admin/dexa-reports/by-appointment';
const VO2MAX_API_URL = 'https://vitaldev.vitalinsights.in/admin/vo2max-reports/by-appointment';
const BLOOD_REPORT_API_URL = 'https://vitaldev.vitalinsights.in/admin/blood-reports/by-appointment';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Helper function to get status class for pill styling
const getStatusClass = (status) => {
  if (!status) return 'pill-normal';
  const statusLower = status.toLowerCase().replace(/\s+/g, '-');
  if (statusLower === 'optimal') return 'pill-optimal';
  if (statusLower === 'at-risk') return 'pill-atrisk';
  if (statusLower === 'elevated') return 'pill-elevated';
  return 'pill-normal';
};

// Helper function to get status and class for a biomarker (with optional gender support)
const getStatusForBiomarker = (biomarkerKey, value, gender = null) => {
  const statusObj = getStatusForValue(biomarkerKey, parseFloat(value), gender);
  if (!statusObj) {
    return { status: 'NO DATA', class: 'pill-normal' };
  }
  return {
    status: statusObj.status,
    class: getStatusClass(statusObj.status)
  };
};

// Biomarker content configuration
const biomarkerContent = {
  fastingInsulin: {
    oneLiner: 'Insulin blocking fat release',
    description: 'When insulin is elevated, it prevents fat cells from releasing fat. Fat loss is biologically blocked and your body is in fat storage mode.',
    whyHappens: 'Frequent eating. High refined-carb intake. Low muscle mass. Insulin resistance. Chronic stress + poor sleep.',
    whatToDo: 'Reduce meal frequency. Prioritise protein-first at every meal. Improve sleep duration and quality. Include strength training.'
  },
  homaIr: {
    oneLiner: 'More insulin needed -> more fat storage',
    description: 'When your cells are insulin resistant, they require more and more insulin to take up glucose. More insulin = More fat storage + Less fat loss, leading to easy fat gain and carb cravings.',
    whyHappens: 'Sedentary lifestyle. Poor sleep. Visceral fat accumulation. Chronic inflammation.',
    whatToDo: 'Strength training to improve insulin sensitivity. Zone 2 aerobic exercise to improve mitochondrial function. Low carb diet. Improve sleep duration and quality.'
  },
  cortisol: {
    oneLiner: 'Stress mediated fat storage',
    description: 'Elevated cortisol can signify Chronic high stress -> which tells your body to store more fat, especially around organs. If your system is stressed, your body prioritizes survival over fat loss. This makes fat loss harder even at low calories.',
    whyHappens: 'Overtraining. Poor sleep. Severe caloric restriction. Chronic psychological stress. Caffeine overuse.',
    whatToDo: 'Improve sleep duration and quality. Do low intensity zone 2 cardio. Incorporate relaxation practices (slow breathing, walking, sunlight exposure)'
  },
  tsh: {
    oneLiner: 'Metabolic down regulation present',
    description: 'Your thyroid controls how much energy your body is willing to burn. Low thyroid hormones (T3 and T4) means fewer calories burned at rest, reduced fat burn and fatigue (less movement)',
    whyHappens: 'Nutrient deficiency (iodine, selenium, zinc, vitamin D). Chronic calorie restriction. Prolonged stress. Autoimmune conditions.',
    whatToDo: 'Avoid very low calorie diets. Use iodised salt in airtight container. Consult with an endocrinologist.'
  },
  freeT3: {
    oneLiner: 'Low thyroid hormone -> Low metabolic rate',
    description: 'Your thyroid controls how much energy your body is willing to burn. Low thyroid hormones (T3 and T4) means fewer calories burned at rest, reduced fat burn and fatigue (less movement)',
    whyHappens: 'Nutrient deficiency (iodine, selenium, zinc, vitamin D). Chronic calorie restriction. Prolonged stress. Autoimmune conditions.',
    whatToDo: 'Avoid very low calorie diets. Use iodised salt in airtight container. Consult with an endocrinologist.'
  },
  hsCrp: {
    oneLiner: 'Chronic low grade inflammation',
    description: 'High systemic inflammation can reduce insulin sensitivity and also increase fat storage. Elevated HsCRP worsens reduced fat burn, signals body to hold more energy and worsens insulin resistance',
    whyHappens: 'Poor sleep, Over training, Processed food and alcohol intake. Visceral fat itself can secrete inflammatory chemicals creating a vicious cycle (Fat -> Inflammation -> Insulin resistance -> More fat storage)',
    whatToDo: 'Improve sleep duration and quality. Eat home cooked whole foods. Reduce processed food intake. Balance training with recovery.'
  }
};

// Helper function to generate a single biomarker card
const generateBiomarkerCard = (marker, status, content, range) => {
  return `
    <div class="box_back">
      <div class="box">
        <div class="box-header">
          <div class="box-title"><div class="box-title1">${marker.value || '--'}</div></div>
          <div class="box-title"><div class="box-title3"></div></div>
        </div>
        <div class="box-title2">${marker.name}</div>

        <div class="boxright ${status.class}">
          <div class="boxright-title1">${status.status}</div>
          <div class="boxright-title2">${range || ''}</div>
        </div>
        <div class="box-content">${content.description}</div>

        <div class="box-content" style="text-transform: uppercase; font-size: 0.65em;"><b>Why does this happen?</b></div>
        <div class="box-content" style="padding-top: 7px;">${content.whyHappens}</div>
        <div class="box-content" style="text-transform: uppercase; font-size: 0.65em;"><b>What should you do?</b></div>
        <div class="box-content" style="padding-top: 7px;">${content.whatToDo}</div>
      </div>
    </div>
  `;
};

// Helper function to get range string for a biomarker (with optional gender support)
const getRangeString = (biomarkerKey, gender = null) => {
  return getRangeStringForBiomarker(biomarkerKey, gender);
};

// Helper function to generate dynamic biomarker cards with pagination
const generateBiomarkerCardsHTML = (formData) => {
  // Extract patient info
  const patientName = formData.patientInfo?.patientName || formData.patient_name || '';
  const patientAge = formData.patientInfo?.age || formData.patient_age || '';
  const patientGender = formData.patientInfo?.gender || formData.patient_gender || '';
  const examDate = formData.patientInfo?.appointmentDate || formData.exam_date || '';
  const patientPhone = formData.patient_phone || '';
  const gender = patientGender;

  const biomarkers = [
    { name: 'Fasting insulin', key: 'fastingInsulin', value: formData.fatLossAssessment?.fastingInsulin || formData.fasting_insulin || '' },
    { name: 'HOMA-IR', key: 'homaIr', value: formData.fatLossAssessment?.homaIr || formData.homa_ir || '' },
    { name: 'Cortisol', key: 'cortisol', value: formData.fatLossAssessment?.cortisol || formData.cortisol || '' },
    { name: 'TSH', key: 'tsh', value: formData.fatLossAssessment?.tsh || formData.tsh || '' },
    { name: 'Free T3', key: 'freeT3', value: formData.fatLossAssessment?.freeT3 || formData.free_t3 || '' },
    { name: 'hs-CRP', key: 'hsCrp', value: formData.fatLossAssessment?.hsCrp || formData.hscrp || '' }
  ];

  // Filter only abnormal biomarkers (AT RISK or ELEVATED, excluding NO DATA and empty values)
  const atRiskBiomarkers = biomarkers.filter(marker => {
    // Skip if no value provided
    if (!marker.value || marker.value === '') return false;
    
    const status = getStatusForBiomarker(marker.key, marker.value, gender);
    // Only include AT RISK and ELEVATED, exclude OPTIMAL and NO DATA
    const statusLower = status.status.toLowerCase();
    return statusLower === 'at risk' || statusLower === 'elevated';
  });

  if (atRiskBiomarkers.length === 0) {
    // Show motivational section when no blockers found (no titles)
    return `
    <div class="box_back">
        <div class="box">
            <div class="box-title2">You're on the Right Track</div>
            <div class="box-content">Congratulations! Your biomarkers are in optimal ranges. This indicates that your current lifestyle, nutrition, and exercise habits are supporting your metabolic health and fat loss goals. Continue maintaining these healthy practices to sustain your progress.</div>
            <div class="box-content" style="margin-top: 15px;"><b>Key Recommendations:</b></div>
            <div class="box-content">• Maintain consistent Zone 2 aerobic training to preserve mitochondrial efficiency</div>
            <div class="box-content">• Continue prioritizing protein intake and strength training</div>
            <div class="box-content">• Sustain your current sleep and stress management practices</div>
            <div class="box-content">• Monitor your biomarkers regularly to ensure continued optimization</div>
        </div>
    </div>
    `;
  }

  let html = '';
  
  // First blocker card (on page 2 with table)
  const firstMarker = atRiskBiomarkers[0];
  const firstStatus = getStatusForBiomarker(firstMarker.key, firstMarker.value, gender);
  const firstContent = biomarkerContent[firstMarker.key];
  const firstRange = getRangeString(firstMarker.key, gender);
  html += generateBiomarkerCard(firstMarker, firstStatus, firstContent, firstRange);

  // Remaining cards: 3 per page
  if (atRiskBiomarkers.length > 1) {
    for (let i = 1; i < atRiskBiomarkers.length; i += 3) {
      const pageNum = Math.floor((i - 1) / 3) + 3;
      
      html += `
    </div>
    <div class="footer">
        <div class="foot_page">${(pageNum - 1).toString().padStart(2, '0')}</div>
    </div>

</page>


<page size="A4" id="blocker-page-${i}">
    <div class="card">
        <div class="header">
            ${patientName}
        </div>
        <hr>
        <div class="details-row">
            <div class="detail-item">
                <span class="icon material-symbols-outlined">person</span>
                <span class="value">${patientAge} YEARS</span>
            </div>
            <div class="detail-item">
                <span class="icon material-symbols-outlined">man</span>
                <span class="value">${patientGender}</span>
            </div>
            <div class="detail-item">
                <span class="icon material-symbols-outlined">calendar_month</span>
                <span class="value">${examDate}</span>
            </div>
            <div class="detail-item">
                <span class="icon material-symbols-outlined"  style="font-variation-settings: 'FILL' 1">call</span>
                <span class="value">${patientPhone}</span>
            </div>

        </div>
    </div>
    <div class="container">
        <div class="report-header">
            <span class="con"></span>
            <p>What's blocking your fat loss</p>
        </div>`;
      
      // Add up to 3 cards per page
      for (let j = 0; j < 3 && i + j < atRiskBiomarkers.length; j++) {
        const marker = atRiskBiomarkers[i + j];
        const status = getStatusForBiomarker(marker.key, marker.value, gender);
        const content = biomarkerContent[marker.key];
        const range = getRangeString(marker.key, gender);
        html += generateBiomarkerCard(marker, status, content, range);
      }
      
      html += `
    </div>
    <div class="footer">
        <div class="foot_page">${pageNum.toString().padStart(2, '0')}</div>
    </div>

</page>`;
    }
  }

  return html;
};

// Helper function to generate Energy Balance page HTML
const generateEnergyBalancePageHTML = (formData, restingMetabolicRate, targetCalories, zone2HeartRate, patientName, patientAge, patientGender, examDate) => {
  const hasRMR = restingMetabolicRate && restingMetabolicRate !== '';
  const hasZone2 = zone2HeartRate && zone2HeartRate !== '';

  // If both values are missing, don't generate the page at all
  if (!hasRMR && !hasZone2) {
    return '';
  }

  let pageHTML = `
<page size="A4" id="page-4">
    <div class="card">
        <div class="header">
            ${patientName}
        </div>
        <hr>
        <div class="details-row">
            <div class="detail-item">
                <span class="icon material-symbols-outlined">person</span>
                <span class="value">${patientAge} YEARS</span>
            </div>
            <div class="detail-item">
                <span class="icon material-symbols-outlined">man</span>
                <span class="value">${patientGender}</span>
            </div>
            <div class="detail-item">
                <span class="icon material-symbols-outlined">calendar_month</span>
                <span class="value">${examDate}</span>
            </div>
            <div class="detail-item">
                <span class="icon material-symbols-outlined"  style="font-variation-settings: 'FILL' 1">call</span>
                <span class="value">{{patient_phone}}</span>
            </div>
        </div>
    </div>
    <div class="container">
        <div class="page1_header">
            <div class="title">Energy Balance and Exercise Efficiency</div>
            <div class="details">Fat loss is  is about creating the right biological environment for your body to release stored energy. </div>
        </div>
`;

  // Add RMR card if value exists
  if (hasRMR) {
    pageHTML += `
        <div class="box_back">
            <div class="box">
                <div class="box-flex">
                    <div class="box-flex1">
                        <div class="box-header">
                            <div class="box-subicon box-icon6"></div>
                            <div class="box-title"><div class="box-title2" style="margin-top: 7px;">Your daily energy baseline - RMR</div></div>
                        </div>

                        <div class="box-content">This target supports sustainable fat loss while preserving metabolic function. Consuming below this threshold triggers metabolic adaptation (lower RMR), making long term weight maintenance difficult. </div>

                        <div class="box-header" style="margin-top: 20px;">
                            <div class="box-subicon box-icon7"></div>
                            <div class="box-title"><div class="box-title2" style="margin-top: 7px;">Resting Metabolic rate: ${restingMetabolicRate} Kcals/day </div></div>
                        </div>

                        <div class="box-content">Your Resting Metabolic Rate represents the number of calories your body needs each day to sustain vital functions at rest like breathing, circulation, and cellular repair. It forms the foundation of your energy balance.</div>
                    </div>

                    <div class="box-flex2">
                        <div class="boxflexright">
                            <div class="box-title2" style="font-size: 0.75em;">target calories</div>
                            <div class="box-title1">${targetCalories}</div>
                            <div class="boxright-title2" style="font-size: 0.75em; text-transform: uppercase;">kcals/day</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
`;
  }

  // Add Zone 2 card if value exists
  if (hasZone2) {
    pageHTML += `
        <div class="box_back">
            <div class="box">
                <div class="box-flex">
                    <div class="box-flex1">
                        <div class="box-header">
                            <div class="box-subicon box-icon8"></div>
                            <div class="box-title"><div class="box-title2" style="margin-top: 7px;">Zone 2 Heart RatE</div></div>
                        </div>

                        <div class="box-content">Zone 2 training refers to moderate-intensity exercise performed at a heart rate where fat is the primary fuel source. At this intensity, the body improves its ability to burn fat, increase mitochondrial efficiency, and sustain long-term metabolic health. </div>

                        <div class="box-content">Exercising too hard too often can increase stress hormones and reduce fat oxidation. Zone 2 training, done consistently, builds the metabolic capacity required for efficient fat loss.</div>
                    </div>

                    <div class="box-flex2">
                        <div class="boxflexright">
                            <div class="box-title2" style="font-size: 0.75em;">ZONE 2</div>
                            <div class="box-title1">${zone2HeartRate}</div>
                            <div class="boxright-title2" style="font-size: 0.75em; text-transform: uppercase;">BPM</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
`;
  }

  pageHTML += `
    </div>
    <div class="footer">
        <div class="foot_page">04</div>
    </div>

</page>
`;

  return pageHTML;
};

// Helper function to generate plasma biomarkers table HTML
const generatePlasmaMarkersHTML = (formData) => {
  const biomarkers = [
    {
      name: 'Fasting insulin',
      key: 'fastingInsulin',
      value: formData.fatLossAssessment?.fastingInsulin || formData.fasting_insulin || '',
      unit: 'mU/L',
      range: '(0 - 12)',
      description: 'Insulin blocking fat release'
    },
    {
      name: 'HOMA-IR',
      key: 'homaIr',
      value: formData.fatLossAssessment?.homaIr || formData.homa_ir || '',
      unit: '',
      range: '(0 - 2)',
      description: 'More insulin needed -> more fat storage'
    },
    {
      name: 'Cortisol',
      key: 'cortisol',
      value: formData.fatLossAssessment?.cortisol || formData.cortisol || '',
      unit: 'µg/dL',
      range: '(5 - 25)',
      description: 'Stress hormone blocking fat loss'
    },
    {
      name: 'TSH',
      key: 'tsh',
      value: formData.fatLossAssessment?.tsh || formData.tsh || '',
      unit: 'mIU/L',
      range: '(0.4 - 4)',
      description: 'Normal metabolic signaling'
    },
    {
      name: 'Free T3',
      key: 'freeT3',
      value: formData.fatLossAssessment?.freeT3 || formData.free_t3 || '',
      unit: 'pg/mL',
      range: '(2.3 - 4.2)',
      description: 'Thyroid hormone for metabolism'
    },
    {
      name: 'hs-CRP',
      key: 'hsCrp',
      value: formData.fatLossAssessment?.hsCrp || formData.hscrp || '',
      unit: 'mg/L',
      range: '(0 - 1)',
      description: 'Chronic low-grade inflammation'
    }
  ];

  return biomarkers.map(marker => {
    const status = getStatusForBiomarker(marker.key, marker.value);
    const displayValue = marker.value ? `${marker.value} ${marker.unit}` : '--';
    const displayRange = marker.value ? marker.range : marker.range;
    
    return `
      <tr>
        <td>${marker.name}</td>
        <td class="biomarker-table-td-middle"><span class="pill ${status.class}">${status.status}</span></td>
        <td class="biomarker-table-td-middle"><div>${displayValue}</div><div style="font-size: 0.85em; color: #666;">${displayRange}</div></td>
        <td>${marker.value ? marker.description : '--'}</td>
      </tr>
    `;
  }).join('');
};

export const fatLossReportService = {
  // Get report by appointment ID
  getReportByAppointmentId: async (appointmentId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/by-appointment/${appointmentId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching fat loss report:', error);
      throw error;
    }
  },

  // Create new fat loss report
  createReport: async (payload) => {
    try {
      const response = await axios.post(
        API_BASE_URL,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating fat loss report:', error);
      throw error;
    }
  },

  // Update existing fat loss report
  updateReport: async (reportId, payload) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/${reportId}`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating fat loss report:', error);
      throw error;
    }
  },

  // Generate HTML for preview/PDF
  generateFatLossReportHTML: async (formData) => {
    try {
      // Fetch the HTML template
      const templateResponse = await fetch('/templates/fatloss_report_v2.html');
      let html = await templateResponse.text();

      // Handle both snake_case (from form) and camelCase (from API) field names
      const patientName = formData.patientInfo?.patientName || formData.patient_name || '';
      const patientGender = formData.patientInfo?.gender || formData.patient_gender || '';
      const patientAge = formData.patientInfo?.age || formData.patient_age || '';
      const examDate = formData.patientInfo?.appointmentDate || formData.exam_date || '';
      const bodyWeightCms = formData.patientInfo?.bodyWeightCms || formData.body_weight_cms || '';
      const bodyWeightKgs = formData.patientInfo?.bodyWeightKgs || formData.body_weight_kgs || '';

      // Replace patient info placeholders (both lowercase and uppercase versions)
      html = html.replaceAll('{{patient_name}}', patientName);
      html = html.replaceAll('{{PATIENT_NAME}}', patientName);
      html = html.replaceAll('{{patient_gender}}', patientGender);
      html = html.replaceAll('{{PATIENT_GENDER}}', patientGender);
      html = html.replaceAll('{{patient_age}}', patientAge);
      html = html.replaceAll('{{PATIENT_AGE}}', patientAge);
      html = html.replaceAll('{{exam_date}}', examDate);
      html = html.replaceAll('{{EXAM_DATE}}', examDate);
      html = html.replaceAll('{{body_weight_cms}}', bodyWeightCms);
      html = html.replaceAll('{{BODY_WEIGHT_CMS}}', bodyWeightCms);
      html = html.replaceAll('{{body_weight_kgs}}', bodyWeightKgs);
      html = html.replaceAll('{{BODY_WEIGHT_KGS}}', bodyWeightKgs);
      html = html.replaceAll('{{patient_phone}}', formData.patient_phone || '');
      html = html.replaceAll('{{PATIENT_PHONE}}', formData.patient_phone || '');

      // Handle both snake_case and camelCase for biomarker fields
      const bodyFatPercentage = formData.fatLossAssessment?.bodyFatPercentage || formData.body_fat_percentage || '';
      const almi = formData.fatLossAssessment?.almi || formData.almi || '';
      const visceralFatArea = formData.fatLossAssessment?.visceralFatArea || formData.visceral_fat_area || '';
      const astAltRatio = formData.fatLossAssessment?.astAltRatio || formData.ast_alt_ratio || '';
      const triglycerides = formData.fatLossAssessment?.triglycerides || formData.triglycerides || '';
      const fastingInsulin = formData.fatLossAssessment?.fastingInsulin || formData.fasting_insulin || '';
      const homaIr = formData.fatLossAssessment?.homaIr || formData.homa_ir || '';
      const homaIrStatus = "At Risk";
      const cortisol = formData.fatLossAssessment?.cortisol || formData.cortisol || '';
      const tsh = formData.fatLossAssessment?.tsh || formData.tsh || '';
      const freeT3 = formData.fatLossAssessment?.freeT3 || formData.free_t3 || '';
      const hsCrp = formData.fatLossAssessment?.hsCrp || formData.hscrp || '';
      const restingMetabolicRate = formData.fatLossAssessment?.restingMetabolicRate || formData.resting_metabolic_rate || '';
      
      // Calculate target calories based on RMR
      let targetCalories = formData.fatLossAssessment?.targetCalories || formData.target_calories || '';
      if (!targetCalories && restingMetabolicRate) {
        const rmr = parseFloat(restingMetabolicRate);
        if (rmr < 3000) {
          targetCalories = Math.round(rmr * 1.2); // Add 20% if RMR under 3000
        } else {
          targetCalories = Math.round(rmr * 1.1); // Add 10% if RMR 3000 or above
        }
      }
      
      const zone2HeartRate = formData.fatLossAssessment?.zone2HeartRate || formData.zone2_heart_rate || '';
      const blockersSummary = formData.fatLossAssessment?.blockersSummary || formData.blockers_summary || '';
      const recommendations = formData.fatLossAssessment?.recommendations || formData.recommendations || '';

      // Replace fat loss specific fields with values
      html = html.replaceAll('{{body_fat_percentage}}', bodyFatPercentage);
      html = html.replaceAll('{{almi}}', almi);
      html = html.replaceAll('{{visceral_fat_area}}', visceralFatArea);
      html = html.replaceAll('{{ast_alt_ratio}}', astAltRatio);
      html = html.replaceAll('{{triglycerides}}', triglycerides);
      html = html.replaceAll('{{fasting_insulin}}', fastingInsulin);
      html = html.replaceAll('{{homa_ir}}', homaIr);
      html = html.replaceAll('{{homa_ir_status}}', homaIrStatus);
      html = html.replaceAll('{{cortisol}}', cortisol);
      html = html.replaceAll('{{tsh}}', tsh);
      html = html.replaceAll('{{free_t3}}', freeT3);
      html = html.replaceAll('{{hscrp}}', hsCrp);
      html = html.replaceAll('{{resting_metabolic_rate}}', restingMetabolicRate);
      html = html.replaceAll('{{target_calories}}', targetCalories);
      html = html.replaceAll('{{zone2_heart_rate}}', zone2HeartRate);
      html = html.replaceAll('{{blockers_summary}}', blockersSummary);
      html = html.replaceAll('{{recommendations}}', recommendations);

      // Replace status placeholders with dynamic status and class
      const bodyFatStatus = getStatusForBiomarker('bodyFatPercentage', bodyFatPercentage);
      html = html.replaceAll('{{body_fat_status}}', bodyFatStatus.status);
      html = html.replaceAll('{{body_fat_status_class}}', bodyFatStatus.class);
      const bodyFatRange = getRangeString('bodyFatPercentage');
      html = html.replaceAll('{{body_fat_range}}', bodyFatRange);

      const almiStatus = getStatusForBiomarker('almi', almi, patientGender);
      html = html.replaceAll('{{almi_status}}', almiStatus.status);
      html = html.replaceAll('{{almi_status_class}}', almiStatus.class);
      
      // Generate gender-specific ALMI range
      const almiRange = getRangeString('almi', patientGender);
      html = html.replaceAll('{{almi_range}}', almiRange);

      const visceralStatus = getStatusForBiomarker('visceralFatArea', visceralFatArea);
      html = html.replaceAll('{{visceral_fat_status}}', visceralStatus.status);
      html = html.replaceAll('{{visceral_fat_status_class}}', visceralStatus.class);
      const visceralFatRange = getRangeString('visceralFatArea');
      html = html.replaceAll('{{visceral_fat_range}}', visceralFatRange);

      const astAltStatus = getStatusForBiomarker('astAltRatio', astAltRatio);
      html = html.replaceAll('{{ast_alt_status}}', astAltStatus.status);
      html = html.replaceAll('{{ast_alt_status_class}}', astAltStatus.class);
      const astAltRange = getRangeString('astAltRatio');
      html = html.replaceAll('{{ast_alt_range}}', astAltRange);

      const triglyceridesStatus = getStatusForBiomarker('triglycerides', triglycerides);
      html = html.replaceAll('{{triglycerides_status}}', triglyceridesStatus.status);
      html = html.replaceAll('{{triglycerides_status_class}}', triglyceridesStatus.class);
      const triglyceridesRange = getRangeString('triglycerides');
      html = html.replaceAll('{{triglycerides_range}}', triglyceridesRange);

      const fastingInsulinStatus = getStatusForBiomarker('fastingInsulin', fastingInsulin);
      html = html.replaceAll('{{fasting_insulin_status}}', fastingInsulinStatus.status);
      html = html.replaceAll('{{fasting_insulin_status_class}}', fastingInsulinStatus.class);

      // Generate and replace plasma biomarkers table HTML
      const plasmaMarkersHTML = generatePlasmaMarkersHTML(formData);
      html = html.replaceAll('{{plasma_biomarkers}}', plasmaMarkersHTML);

      // Generate and replace dynamic biomarker cards (only for "At Risk" biomarkers)
      const biomarkerCardsHTML = generateBiomarkerCardsHTML(formData);
      html = html.replaceAll('{{biomarker_cards}}', biomarkerCardsHTML);

      // Generate and replace Energy Balance page (conditionally based on RMR and Zone 2 values)
      const energyBalancePageHTML = generateEnergyBalancePageHTML(
        formData,
        restingMetabolicRate,
        targetCalories,
        zone2HeartRate,
        patientName,
        patientAge,
        patientGender,
        examDate
      );
      html = html.replaceAll('{{energy_balance_page}}', energyBalancePageHTML);

      return html;
    } catch (error) {
      console.error('Error generating HTML:', error);
      throw error;
    }
  }
};

// Helper function to find biomarker value and status from Blood Report categories
const findBiomarkerValueAndStatus = (bloodData, biomarkerName) => {
  if (!bloodData.categories || !Array.isArray(bloodData.categories)) {
    return { value: null, status: null };
  }
  
  for (const category of bloodData.categories) {
    if (category.biomarkers && Array.isArray(category.biomarkers)) {
      const biomarker = category.biomarkers.find(b => 
        b.name.toUpperCase().includes(biomarkerName.toUpperCase())
      );
      if (biomarker && biomarker.value && biomarker.value !== '*' && biomarker.value !== '<NA>') {
        return {
          value: parseFloat(biomarker.value),
          status: biomarker.status || null
        };
      }
    }
  }
  return { value: null, status: null };
};

// Prefill function to fetch data from DEXA, VO2Max, and Blood Report APIs
export const prefillFatLossFormData = async (appointmentId) => {
  try {
    const prefillData = {
      patientInfo: {},
      fatLossAssessment: {},
      readOnlyFields: new Set() // Track which fields should be read-only
    };

    // Fetch DEXA report data
    try {
      const dexaResponse = await axios.get(`${DEXA_API_URL}/${appointmentId}`, {
        headers: getAuthHeaders()
      });
      
      if (dexaResponse.data?.data) {
        const dexaData = dexaResponse.data.data;
        
        // Extract patient info from DEXA
        prefillData.patientInfo = {
          patientName: dexaData.patient_name,
          gender: dexaData.gender,
          age: dexaData.age,
          appointmentDate: dexaData.visit_date,
          bodyWeightCms: dexaData.height,
          bodyWeightKgs: dexaData.weight
        };
        
        // Extract biomarker data from DEXA
        prefillData.fatLossAssessment = {
          bodyFatPercentage: dexaData.body_fat_percentage,
          visceralFatArea: dexaData.visceral_fat_area,
          almi: dexaData.almi_number
        };
        
        // Mark DEXA fields as read-only
        prefillData.readOnlyFields.add('body_fat_percentage');
        prefillData.readOnlyFields.add('visceral_fat_area');
        prefillData.readOnlyFields.add('almi');
      }
    } catch (dexaError) {
      console.warn('Error fetching DEXA data:', dexaError.message);
    }

    // Fetch VO2Max report data
    try {
      const vo2Response = await axios.get(`${VO2MAX_API_URL}/${appointmentId}`, {
        headers: getAuthHeaders()
      });
      
      if (vo2Response.data?.data) {
        const vo2Data = vo2Response.data.data;
        
        // Extract patient info from VO2Max (if not already from DEXA)
        if (!prefillData.patientInfo.patientName) {
          prefillData.patientInfo = {
            patientName: vo2Data.patient_name,
            gender: vo2Data.patient_gender,
            age: vo2Data.patient_age,
            appointmentDate: vo2Data.exam_date,
            bodyWeightCms: vo2Data.patient_height,
            bodyWeightKgs: vo2Data.patient_weight
          };
        }
        
        // Extract biomarker data from VO2Max
        const zone2Range = vo2Data.training_zones?.zone2_bpm || '';
        const zone2HeartRate = zone2Range || '';
        
        prefillData.fatLossAssessment = {
          ...prefillData.fatLossAssessment,
          zone2HeartRate: zone2HeartRate,
          restingMetabolicRate: vo2Data.rmr_value || ''
        };
        
        // Mark VO2Max fields as read-only
        if (zone2HeartRate) prefillData.readOnlyFields.add('zone2_heart_rate');
        if (vo2Data.rmr_value) prefillData.readOnlyFields.add('resting_metabolic_rate');
      }
    } catch (vo2Error) {
      console.warn('Error fetching VO2Max data:', vo2Error.message);
    }

    // Fetch Blood Report data
    try {
      const bloodResponse = await axios.get(`${BLOOD_REPORT_API_URL}/${appointmentId}`, {
        headers: getAuthHeaders()
      });
      
      if (bloodResponse.data?.data) {
        const bloodData = bloodResponse.data.data;
        
        // Extract patient info from Blood Report (if not already from DEXA or VO2Max)
        if (!prefillData.patientInfo.patientName) {
          prefillData.patientInfo = {
            patientName: bloodData.name,
            gender: bloodData.gender === 'M' ? 'Male' : bloodData.gender === 'F' ? 'Female' : bloodData.gender,
            age: bloodData.age,
            appointmentDate: bloodData.visit_date
          };
        }
        
        // Extract biomarker data and status from Blood Report
        const triglyceridesData = findBiomarkerValueAndStatus(bloodData, 'TRIGLYCERIDES');
        const cortisolData = findBiomarkerValueAndStatus(bloodData, 'CORTISOL');
        const tshData = findBiomarkerValueAndStatus(bloodData, 'TSH');
        const freeT3Data = findBiomarkerValueAndStatus(bloodData, 'FREE T3');
        const hsCrpData = findBiomarkerValueAndStatus(bloodData, 'hsCRP');
        const homaIrData = findBiomarkerValueAndStatus(bloodData, 'INSULIN RESISTANCE');
        
        prefillData.fatLossAssessment = {
          ...prefillData.fatLossAssessment,
          triglycerides: triglyceridesData.value || prefillData.fatLossAssessment.triglycerides,
          triglycerides_status: triglyceridesData.status,
          cortisol: cortisolData.value || prefillData.fatLossAssessment.cortisol,
          cortisol_status: cortisolData.status,
          tsh: tshData.value || prefillData.fatLossAssessment.tsh,
          tsh_status: tshData.status,
          freeT3: freeT3Data.value || prefillData.fatLossAssessment.freeT3,
          freeT3_status: freeT3Data.status,
          hscrp: hsCrpData.value || prefillData.fatLossAssessment.hscrp,
          hscrp_status: hsCrpData.status,
          homaIr: homaIrData.value || prefillData.fatLossAssessment.homaIr,
          homaIr_status: homaIrData.status
        };
        
        // Mark Blood Report fields as read-only
        if (triglyceridesData.value) prefillData.readOnlyFields.add('triglycerides');
        if (cortisolData.value) prefillData.readOnlyFields.add('cortisol');
        if (tshData.value) prefillData.readOnlyFields.add('tsh');
        if (freeT3Data.value) prefillData.readOnlyFields.add('free_t3');
        if (hsCrpData.value) prefillData.readOnlyFields.add('hscrp');
        if (homaIrData.value) prefillData.readOnlyFields.add('homa_ir');
      }
    } catch (bloodError) {
      console.warn('Error fetching Blood Report data:', bloodError.message);
    }

    // If no data from external reports, fetch from Fat Loss API
    if (Object.keys(prefillData.fatLossAssessment).length === 0) {
      try {
        console.log('No data from external reports, fetching from Fat Loss API...');
        const fatLossResponse = await fatLossReportService.getReportByAppointmentId(appointmentId);
        
        if (fatLossResponse?.data) {
          const fatLossData = fatLossResponse.data;
          
          // Extract all biomarker data from Fat Loss API
          prefillData.fatLossAssessment = {
            bodyFatPercentage: fatLossData.body_fat_percentage,
            visceralFatArea: fatLossData.visceral_fat_area,
            almi: fatLossData.almi,
            triglycerides: fatLossData.triglycerides,
            cortisol: fatLossData.cortisol,
            tsh: fatLossData.tsh,
            freeT3: fatLossData.free_t3,
            hscrp: fatLossData.hscrp,
            homaIr: fatLossData.homa_ir,
            zone2HeartRate: fatLossData.zone2_heart_rate,
            restingMetabolicRate: fatLossData.resting_metabolic_rate
          };
          
          // Fat Loss API data is editable (not read-only)
          console.log('Data loaded from Fat Loss API - fields are editable');
        }
      } catch (fatLossError) {
        console.warn('Error fetching Fat Loss API data:', fatLossError.message);
      }
    }

    return prefillData;
  } catch (error) {
    console.error('Error prefilling form data:', error);
    throw error;
  }
};

// Export the HTML generation function separately for convenience
export const generateFatLossReportHTML = fatLossReportService.generateFatLossReportHTML;
